import { Form, FormGroup, Label, Input, Row, Col, Button } from "reactstrap";
import Select from "react-select";

import { useFormik } from "formik";
import { SettingsProps } from "../../types/settings";
import {
  useGetOrganizationsById,
  useUpdateOrganizations,
} from "hooks/useOrganization";
import { RootState } from "slices";
import { useSelector } from "react-redux";
import { Organization } from "types/organization";
import { toast } from "react-toastify";
import { OrganizationValidationSchema } from "utils/rules/organization";
import Flatpickr from "react-flatpickr";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch } from "react-redux";
import { setDefaults } from "slices/auth/login/reducer";

interface numberOptions {
  value: string;
  label: string;
}
interface languageOptions {
  value: string;
  label: string;
}
interface currencyOptions {
  value: string;
  label: string;
}
interface time_zoneOptions {
  value: string;
  label: string;
}

interface time_formatOptions {
  value: string;
  label: string;
}
interface date_formatOptions {
  value: string;
  label: string;
}

const GeneralSettings: React.FC<SettingsProps> = ({
  onGlobalCancel,
  isSaving,
  onSave,
}) => {
  const dispatch: any = useDispatch();

  // Define options for select inputs
  const currencyOptions: currencyOptions[] = [
    { value: "GHS", label: "GHS - Ghanaian Cedi" },
    { value: "USD", label: "USD - US Dollar" },
    { value: "EUR", label: "EUR - Euro" },
  ];

  const timezoneOptions: time_zoneOptions[] = [
    { value: "GMT", label: "GMT - Greenwich Mean Time" },
    { value: "EST", label: "EST - Eastern Standard Time" },
    { value: "PST", label: "PST - Pacific Standard Time" },
  ];

  const languageOptions: languageOptions[] = [
    { value: "en", label: "English" },
    { value: "fr", label: "French" },
    { value: "es", label: "Spanish" },
  ];

  const numberFormatOptions: numberOptions[] = [
    { value: "1,234.56", label: "1,234.56" },
    { value: "1.234,56", label: "1.234,56" },
  ];

  const timeformatOptions: time_formatOptions[] = [
    { value: "12", label: "12-hour (AM/PM)" },
    { value: "24", label: "24-hour" },
  ];
  const dateformatOptions: date_formatOptions[] = [
    { value: "MM/DD/YYYY", label: "MM/DD/YYYY" },
    { value: "DD/MM/YYYY", label: "DD/MM/YYYY" },
    { value: "YYYY-MM-DD", label: "YYYY-MM-DD" },
  ];

  const { mutate } = useUpdateOrganizations();

  const selectedOrganization = useSelector(
    (state: RootState) => state.Login?.user
  );

  console.log(selectedOrganization.organization_id);
  const { data: selectedOrganizationData } = useGetOrganizationsById(
    String(selectedOrganization?.organization_id)
  );
  console.log(selectedOrganizationData);

  const formik = useFormik<Organization["org"]>({
    initialValues: {
      id: selectedOrganizationData?.id,
      name: selectedOrganizationData?.name,
      address: selectedOrganizationData?.address,
      business_email: selectedOrganizationData?.business_email,
      phone_number: selectedOrganizationData?.phone_number,
      default_currency: selectedOrganizationData?.default_currency,
      timezone: selectedOrganizationData?.timezone,
      default_language: selectedOrganizationData?.default_language,
      date_format: selectedOrganizationData?.date_format,
      time_format: selectedOrganizationData?.time_format,
      number_format: selectedOrganizationData?.number_format,
      fiscal_year_start: selectedOrganizationData?.fiscal_year_start,
      tax_id: selectedOrganizationData?.tax_id,
      website: selectedOrganizationData?.website,
    },
    enableReinitialize: true,

    validationSchema: OrganizationValidationSchema,

    onSubmit: async (values) => {
      const payload = {
        id: values.id,
        name: values.name,
        address: values.address,
        business_email: values.business_email,
        phone_number: values.phone_number,
        default_currency: values.default_currency,
        timezone: values.timezone,
        default_language: values.default_language,
        date_format: values.date_format,
        time_format: values.time_format,
        number_format: values.number_format,
        fiscal_year_start: values.fiscal_year_start,
        tax_id: values.tax_id,
        website: values.website,
      };

      //mutate for updating the organization form data
      mutate(
        { id: selectedOrganizationData?.id, data: payload },
        {
          onSuccess: (data) => {
            dispatch(
              setDefaults({
                default_currency: data?.default_currency,
                default_language: data?.default_language,
              })
            );
            toast.success("General settings updated successfully.");
          },
        }
      );
    },
  });

  const handleCancel = () => {
    formik.resetForm();
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center">
        <h5 className="mb-4">General Settings</h5>
      </div>
      <Form onSubmit={formik.handleSubmit}>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="name">Organization Name</Label>
              <Input
                type="text"
                id="business_name"
                name="name"
                placeholder="Enter your organization name"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.name}
                invalid={formik.touched.name && !!formik.errors.name}
              />
              {formik.touched.name && formik.errors.name && (
                <div className="text-danger">{formik.errors.name}</div>
              )}
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="email">Organization Email</Label>
              <Input
                type="email"
                id="email"
                name="business_email"
                placeholder="Enter your business email"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.business_email}
                invalid={
                  formik.touched.business_email &&
                  !!formik.errors.business_email
                }
              />
              {formik.touched.business_email &&
                formik.errors.business_email && (
                  <div className="text-danger">
                    {formik.errors.business_email}
                  </div>
                )}
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="phone">Phone Number</Label>
              <Input
                type="tel"
                id="phone_number"
                name="phone_number"
                placeholder="Enter organization phone number"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.phone_number}
                invalid={
                  !!(formik.touched.phone_number && formik.errors.phone_number)
                }
              />
              {formik.touched.phone_number && formik.errors.phone_number && (
                <div className="text-danger">{formik.errors.phone_number}</div>
              )}
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="default_currency">Default Currency</Label>
              <Select
                id="default_currency"
                name="default_currency"
                options={currencyOptions}
                value={currencyOptions?.find(
                  (b) => b.value === formik.values.default_currency
                )}
                onChange={(option) => {
                  formik.setFieldValue("default_currency", option?.value);
                }}
                className={
                  formik.touched.default_currency &&
                  formik.errors.default_currency
                    ? "is-invalid"
                    : ""
                }
              />
              {formik.touched.default_currency &&
                formik.errors.default_currency && (
                  <div className="text-danger">
                    {formik.errors.default_currency}
                  </div>
                )}
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <FormGroup>
              <Label for="address">organization Address</Label>
              <Input
                type="textarea"
                id="address"
                name="address"
                rows={3}
                placeholder="Enter your organization address"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.address}
                invalid={formik.touched.address && !!formik.errors.address}
              />
              {formik.touched.address && formik.errors.address && (
                <div className="text-danger">{formik.errors.address}</div>
              )}
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="timezone">Timezone</Label>
              <Select
                id="timezone"
                name="timezone"
                value={timezoneOptions?.find(
                  (b) => b.value === formik.values.timezone
                )}
                onChange={(option) => {
                  formik.setFieldValue("timezone", option?.value);
                }}
                options={timezoneOptions}
                className={
                  formik.touched.timezone && formik.errors.timezone
                    ? "is-invalid"
                    : ""
                }
              />
              {formik.touched.timezone && formik.errors.timezone && (
                <div className="text-danger">{formik.errors.timezone}</div>
              )}
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="language">Default Language</Label>
              <Select
                id="default_language"
                name="default_language"
                options={languageOptions}
                value={languageOptions?.find(
                  (b) => b.value === formik.values.default_language
                )}
                onChange={(option) => {
                  formik.setFieldValue("default_language", option?.value);
                }}
                className={
                  formik.touched.default_language &&
                  formik.errors.default_language
                    ? "is-invalid"
                    : ""
                }
              />
              {formik.touched.default_language &&
                formik.errors.default_language && (
                  <div className="text-danger">
                    {formik.errors.default_language}
                  </div>
                )}
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="dateFormat">Date Format</Label>
              <Select
                id="date_format"
                name="date_format"
                options={dateformatOptions}
                value={dateformatOptions?.find(
                  (b) => b.value === formik.values.date_format
                )}
                onChange={(option) => {
                  formik.setFieldValue("date_format", option?.value);
                }}
                className={
                  formik.touched.date_format && formik.errors.date_format
                    ? "is-invalid"
                    : ""
                }
              />
              {formik.touched.date_format && formik.errors.date_format && (
                <div className="text-danger">{formik.errors.date_format}</div>
              )}
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="time_format">Time Format</Label>
              <Select
                id="time_format"
                name="time_format"
                options={timeformatOptions}
                value={timeformatOptions?.find(
                  (b) => b.value === formik.values.time_format
                )}
                onChange={(option) => {
                  formik.setFieldValue("time_format", option?.value);
                }}
                className={
                  formik.touched.time_format && formik.errors.time_format
                    ? "is-invalid"
                    : ""
                }
              />
              {formik.touched.time_format && formik.errors.time_format && (
                <div className="text-danger">{formik.errors.time_format}</div>
              )}
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="number_format">Number Format</Label>
              <Select
                id="number_format"
                name="number_format"
                value={numberFormatOptions?.find(
                  (b) => b.value === formik.values.number_format
                )}
                onChange={(option) => {
                  formik.setFieldValue("number_format", option?.value);
                }}
                options={numberFormatOptions}
                className={
                  formik.touched.number_format && formik.errors.number_format
                    ? "is-invalid"
                    : ""
                }
              />
              {formik.touched.number_format && formik.errors.number_format && (
                <div className="text-danger">{formik.errors.number_format}</div>
              )}
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="fiscal_year_start">Fiscal Year Start</Label>
              <Flatpickr
                id="fiscal_year_start"
                name="fiscal_year_start"
                value={formik.values.fiscal_year_start || undefined}
                placeholder="Select fiscal year start"
                onChange={(date: Date[]) => {
                  const formattedDate = date.length
                    ? date[0].toISOString().split("T")[0]
                    : null;
                  formik.setFieldValue("fiscal_year_start", formattedDate);
                }}
                options={{
                  dateFormat: "Y-m-d",
                  altFormat: "F j, Y",
                  altInput: true,
                }}
                className="form-control"
              />
              {formik.touched.fiscal_year_start &&
                formik.errors.fiscal_year_start && (
                  <div className="text-danger">
                    {formik.errors.fiscal_year_start}
                  </div>
                )}
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="tax_id">Tax ID/VAT Number</Label>
              <Input
                type="text"
                id="tax_id"
                name="tax_id"
                placeholder="Enter your tax ID or VAT number"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.tax_id}
                invalid={formik.touched.tax_id && !!formik.errors.tax_id}
              />
              {formik.touched.tax_id && formik.errors.tax_id && (
                <div className="text-danger">{formik.errors.tax_id}</div>
              )}
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="website">Organization Website</Label>
              <Input
                type="text"
                id="website"
                name="website"
                placeholder="https://www.example.com"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.website}
                invalid={formik.touched.website && !!formik.errors.website}
              />
              {formik.touched.website && formik.errors.website && (
                <div className="text-danger">{formik.errors.website}</div>
              )}
            </FormGroup>
          </Col>
        </Row>

        <Button
          color="secondary"
          className="w-10 me-1"
          style={{ width: "20%" }}
          onClick={handleCancel} // resets the form field
        >
          Reset
        </Button>

        <Button
          type="submit"
          color="primary"
          className="w-10 ms-1"
          style={{ width: "20%" }}
          onClick={() => {
            formik.handleSubmit();
          }}
        >
          Save Changes
        </Button>
      </Form>
    </div>
  );
};

export default GeneralSettings;
